import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiEdit2, FiLayers, FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const LEVELS = [
  { value: "family", label: "Families", singular: "Family", rowsKey: "families", parentField: null, childLabel: "Subfamilies" },
  { value: "subfamily", label: "Subfamilies", singular: "Subfamily", rowsKey: "subfamilies", parentField: "family_id", childLabel: "Categories" },
  { value: "category", label: "Categories", singular: "Category", rowsKey: "categories", parentField: "subfamily_id", childLabel: "Subcategories" },
  { value: "subcategory", label: "Subcategories", singular: "Subcategory", rowsKey: "subcategories", parentField: "category_id", childLabel: "Children" },
];

const EMPTY_TAXONOMY = { families: [], subfamilies: [], categories: [], subcategories: [] };

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function classificationLabel(row) {
  return row ? `${row.code || row.id} - ${row.name}` : "";
}

function usageOf(row) {
  return {
    product_count: Number(row?.usage?.product_count || 0),
    child_count: Number(row?.usage?.child_count || 0),
    mapping_count: Number(row?.usage?.mapping_count || 0),
    forecast_count: Number(row?.usage?.forecast_count || 0),
  };
}

function dependencyTotal(row) {
  return Object.values(usageOf(row)).reduce((sum, value) => sum + value, 0);
}

function parentPath(level, row) {
  if (level === "family") return "Top level";
  if (level === "subfamily") return row.family ? classificationLabel(row.family) : "No family";
  if (level === "category") {
    const familyName = row.subfamily?.family?.name;
    const subfamilyName = row.subfamily?.name;
    return [familyName, subfamilyName].filter(Boolean).join(" / ") || "No subfamily";
  }
  const familyName = row.category?.subfamily?.family?.name;
  const subfamilyName = row.category?.subfamily?.name;
  const categoryName = row.category?.name;
  return [familyName, subfamilyName, categoryName].filter(Boolean).join(" / ") || "No category";
}

export default function Classifications() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [taxonomy, setTaxonomy] = useState(EMPTY_TAXONOMY);
  const [level, setLevel] = useState("family");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState({ level: "family", name: "", parent_id: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentLevel = LEVELS.find((option) => option.value === level) || LEVELS[0];

  const loadTaxonomy = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/catalog/taxonomy", {
        params: { business_unit_id: businessUnitId },
      });
      setTaxonomy(res.data || EMPTY_TAXONOMY);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load POS classifications.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId]);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);

  const rows = useMemo(() => {
    const source = taxonomy[currentLevel.rowsKey] || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return source;
    return source.filter((row) =>
      [row.code, row.name, parentPath(level, row)].some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [currentLevel.rowsKey, level, searchQuery, taxonomy]);

  const editorLevel = LEVELS.find((option) => option.value === form.level) || LEVELS[0];
  const parentOptions = useMemo(() => {
    if (form.level === "subfamily") return taxonomy.families || [];
    if (form.level === "category") return taxonomy.subfamilies || [];
    if (form.level === "subcategory") return taxonomy.categories || [];
    return [];
  }, [form.level, taxonomy]);

  const openCreate = () => {
    setError("");
    setMessage("");
    setForm({ level, name: "", parent_id: "" });
    setEditor({ mode: "create" });
  };

  const openEdit = (row) => {
    const config = LEVELS.find((option) => option.value === level) || LEVELS[0];
    setError("");
    setMessage("");
    setForm({
      level,
      name: row.name || "",
      parent_id: config.parentField ? String(row[config.parentField] || "") : "",
    });
    setEditor({ mode: "edit", row });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditor(null);
    setError("");
  };

  const closeDeleteModal = () => {
    if (saving) return;
    setDeleteTarget(null);
    setError("");
  };

  const saveClassification = async () => {
    const name = form.name.trim();
    if (!name) {
      setError(`${editorLevel.singular} name is required.`);
      return;
    }
    if (editorLevel.parentField && !form.parent_id) {
      setError(`${editorLevel.singular} parent is required.`);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        business_unit_id: businessUnitId,
        name,
        ...(editorLevel.parentField ? { [editorLevel.parentField]: form.parent_id } : {}),
      };

      if (editor?.mode === "edit") {
        await axios.patch(`/api/pos/catalog/classifications/${form.level}/${editor.row.id}`, payload);
        setMessage(`${editorLevel.singular} updated.`);
      } else {
        const endpoint = {
          family: "/api/pos/catalog/families",
          subfamily: "/api/pos/catalog/subfamilies",
          category: "/api/pos/catalog/categories",
          subcategory: "/api/pos/catalog/subcategories",
        }[form.level];
        await axios.post(endpoint, payload);
        setMessage(`${editorLevel.singular} created.`);
        setLevel(form.level);
      }
      await loadTaxonomy();
      setEditor(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save POS classification.");
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (row) => {
    setError("");
    setMessage("");
    setDeleteTarget({ level, row });
  };

  const deleteClassification = async () => {
    if (!deleteTarget?.row?.id || dependencyTotal(deleteTarget.row)) return;
    try {
      setSaving(true);
      setError("");
      const res = await axios.delete(
        `/api/pos/catalog/classifications/${deleteTarget.level}/${deleteTarget.row.id}`,
        { params: { business_unit_id: businessUnitId } }
      );
      if (res.data?.deleted) {
        const deletedLevel = LEVELS.find((option) => option.value === deleteTarget.level) || LEVELS[0];
        setMessage(`${deletedLevel.singular} deleted.`);
      }
      setDeleteTarget(null);
      await loadTaxonomy();
    } catch (err) {
      const dependencies = err?.response?.data?.dependencies;
      if (dependencies) {
        setDeleteTarget((current) => ({
          ...current,
          row: { ...current.row, usage: dependencies },
        }));
      }
      setError(err?.response?.data?.error || "Could not delete POS classification.");
    } finally {
      setSaving(false);
    }
  };

  const deleteUsage = usageOf(deleteTarget?.row);
  const canDelete = deleteTarget && dependencyTotal(deleteTarget.row) === 0;

  return (
    <div className="reservations-container rm-page posClassificationsPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Classifications</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading classifications..." : "POS product taxonomy"}
            </div>
          </div>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreate}>
              <FiPlus /> Add classification
            </button>
          </div>
        </div>
      </div>

      {error && !editor && !deleteTarget ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header posClassificationsHeader">
          <div>
            <h3 className="rm-rates-title">Classification hierarchy</h3>
            <div className="rm-rates-subtitle">Create, rename, move or remove POS classifications</div>
          </div>
          <div className="posClassificationSearch">
            <FiSearch aria-hidden="true" />
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search code, name or parent"
            />
          </div>
        </div>

        <div className="posClassificationLevelTabs" role="tablist" aria-label="Classification level">
          {LEVELS.map((option) => {
            const selected = level === option.value;
            const rowCount = taxonomy[option.rowsKey]?.length || 0;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`posClassificationLevelTab ${selected ? "posClassificationLevelTab--active" : ""}`}
                onClick={() => setLevel(option.value)}
              >
                <FiLayers />
                <span>{option.label}</span>
                <strong>{rowCount.toLocaleString("es-ES")}</strong>
              </button>
            );
          })}
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading..." : `${rows.length.toLocaleString("es-ES")} ${currentLevel.label.toLowerCase()}`}
          </small>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table posClassificationsTable" style={{ "--rm-col1-width": "260px", "--rm-table-min-width": "1120px" }}>
            <colgroup>
              <col style={{ width: "260px" }} />
              <col style={{ width: "300px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "130px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th rm-sticky-1"><div className="res-thBtn"><span className="res-thLabel">Classification</span><span className="res-thSub">Code / name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Parent</span><span className="res-thSub">Hierarchy</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Products</span><span className="res-thSub">Assigned</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">{currentLevel.childLabel}</span><span className="res-thSub">Direct children</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Cost mappings</span><span className="res-thSub">Analytics</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Forecasts</span><span className="res-thSub">Saved</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Manage</span></div></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const usage = usageOf(row);
                return (
                  <tr key={`${level}:${row.id}`}>
                    <td className="rm-sticky-1">
                      <div className="posClassificationName">
                        <strong>{row.name}</strong>
                        <span>{row.code || row.id}</span>
                      </div>
                    </td>
                    <td><span className="posClassificationPath">{parentPath(level, row)}</span></td>
                    <td>{usage.product_count.toLocaleString("es-ES")}</td>
                    <td>{level === "subcategory" ? "-" : usage.child_count.toLocaleString("es-ES")}</td>
                    <td>{usage.mapping_count.toLocaleString("es-ES")}</td>
                    <td>{usage.forecast_count.toLocaleString("es-ES")}</td>
                    <td>
                      <div className="posDeviceActions posClassificationActions">
                        <button type="button" className="posTicketIconBtn posTicketIconBtn--edit" onClick={() => openEdit(row)} title="Edit classification" aria-label={`Edit ${row.name}`}>
                          <FiEdit2 />
                        </button>
                        <button type="button" className="posTicketIconBtn posTicketIconBtn--danger" onClick={() => requestDelete(row)} title="Delete classification" aria-label={`Delete ${row.name}`}>
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!rows.length ? (
                <tr><td colSpan="7" className="posMuted">No classifications found.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {editor ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posProductModal posClassificationEditorModal" role="dialog" aria-modal="true" aria-label={`${editor.mode === "edit" ? "Edit" : "Add"} classification`}>
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">{editor.mode === "edit" ? "Edit classification" : "Add classification"}</h3>
                <div className="rm-rates-subtitle">POS product taxonomy</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeEditor} aria-label="Close"><FiX /></button>
            </div>
            {error ? <div className="posStateBanner">{error}</div> : null}
            <div className="posProductModalBody">
              <div className="posModalFormGrid">
                <label className="posModalField">
                  <span>Type</span>
                  <select
                    className="posSelect"
                    value={form.level}
                    disabled={editor.mode === "edit" || saving}
                    onChange={(event) => setForm({ level: event.target.value, name: "", parent_id: "" })}
                  >
                    {LEVELS.map((option) => <option key={option.value} value={option.value}>{option.singular}</option>)}
                  </select>
                </label>
                {editorLevel.parentField ? (
                  <label className="posModalField">
                    <span>Parent</span>
                    <select
                      className="posSelect"
                      value={form.parent_id}
                      disabled={saving}
                      onChange={(event) => setForm((current) => ({ ...current, parent_id: event.target.value }))}
                    >
                      <option value="">Select parent</option>
                      {parentOptions.map((row) => <option key={row.id} value={row.id}>{classificationLabel(row)}</option>)}
                    </select>
                  </label>
                ) : null}
                <label className="posModalField posModalField--wide">
                  <span>Name</span>
                  <input
                    className="posTextInput"
                    value={form.name}
                    disabled={saving}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    placeholder="Classification name"
                    autoFocus
                  />
                </label>
              </div>
            </div>
            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeEditor} disabled={saving}><FiX /> Cancel</button>
              <button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveClassification} disabled={saving || !form.name.trim() || (editorLevel.parentField && !form.parent_id)}>
                {saving ? "Saving" : editor.mode === "edit" ? "Save changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posProductModal posClassificationDeleteModal" role="dialog" aria-modal="true" aria-label="Delete classification">
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">Delete classification</h3>
                <div className="rm-rates-subtitle">{deleteTarget.row.name}</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeDeleteModal} aria-label="Close"><FiX /></button>
            </div>
            {error ? <div className="posStateBanner">{error}</div> : null}
            <div className="posProductModalBody">
              {canDelete ? (
                <div className="posClassificationDeleteCopy">
                  <FiTrash2 />
                  <div>
                    <strong>Delete {deleteTarget.row.name}?</strong>
                    <span>This classification is empty and can be removed safely.</span>
                  </div>
                </div>
              ) : (
                <div className="posClassificationDeleteBlocked">
                  <FiAlertTriangle />
                  <div>
                    <strong>This classification is in use</strong>
                    <span>Reassign or remove these dependencies before deleting it.</span>
                  </div>
                  <div className="posClassificationDependencyList">
                    {deleteUsage.product_count ? <span><b>Products</b><strong>{deleteUsage.product_count.toLocaleString("es-ES")}</strong></span> : null}
                    {deleteUsage.child_count ? <span><b>Child classifications</b><strong>{deleteUsage.child_count.toLocaleString("es-ES")}</strong></span> : null}
                    {deleteUsage.mapping_count ? <span><b>Cost mappings</b><strong>{deleteUsage.mapping_count.toLocaleString("es-ES")}</strong></span> : null}
                    {deleteUsage.forecast_count ? <span><b>Forecasts</b><strong>{deleteUsage.forecast_count.toLocaleString("es-ES")}</strong></span> : null}
                  </div>
                </div>
              )}
            </div>
            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeDeleteModal} disabled={saving}>Close</button>
              {canDelete ? (
                <button type="button" className="rm-topBtn rm-topBtn--amber" onClick={deleteClassification} disabled={saving}>
                  <FiTrash2 /> {saving ? "Deleting" : "Delete"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
